import { ratingStrings } from './ratings';

export const moduleCommonStrings = {
  imports: {
    batchLimit: (count) => `Select up to ${count} entries per import.`,
    moreWithPro: 'Get Pro to import more',
    upsellTitle: 'Import more with Pro',
    upsellDetail: 'Pro removes the free entry limit for this hobby.',
    selectionFull: (count) => `You can select up to ${count} entries for this import.`,
    showMore: 'Show more',
    alreadyImported: 'Already imported',
    noItems: 'No entries found in this export. Choose another file.',
    allDuplicates: 'Everything in this export is already saved.',
    changeFile: 'Change file',
    retry: 'Try again',
    fileErrorHelp: 'Check the export and choose another file.',
  },
  actions: {
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    reset: 'Reset',
    done: 'Done',
    select: 'Select',
    addManually: 'Add manually',
    createNew: 'Create new',
    remove: 'Remove',
    saving: 'Saving...',
    deleting: 'Deleting...',
    searching: 'Searching...',
    useExisting: 'Use existing',
    back: 'Back',
    next: 'Next',
  },
  filters: {
    favorites: 'Favorites',
    noOptions: 'No options yet',
    tryDifferentSearchOrFilter: 'Try a different search or clear your filters.',
  },
  empty: {
    nothingSaved: 'Nothing saved yet',
    noMatches: 'No matches found',
    first: (item) => `Add your first ${item} when you are ready.`,
  },
  labels: {
    required: 'Required',
  },
  delete: {
    cannotUndo: 'This cannot be undone.',
  },
  favorite: {
    mark: 'Mark as favorite',
  },
  sharing: {
    sharedWithYou: 'Shared with you',
    canEdit: 'Can edit',
    viewOnly: 'View only',
    sharedByFallback: 'another Hobtalk user',
    accessLine: (accessLevel) => `Shared with you · ${accessLevel === 'edit' ? 'Can edit' : 'View only'}`,
    sharedBy: (name) => `Shared by ${name || 'another Hobtalk user'}`,
  },
};

export const cookingStrings = {
  title: 'Cooking',
  actions: {
    createManually: 'Create manually',
    importFromUrl: 'Import from web',
    addRecipe: 'Add recipe',
    import: 'Import',
    save: 'Save recipe',
  },
  searchPlaceholder: 'Search recipes',
  empty: {
    loadError: 'Unable to load recipes',
    title: 'Nothing saved yet',
    first: 'Add your first recipe when you are ready.',
  },
  delete: {
    title: 'Delete recipe',
    alertTitle: 'Delete recipe?',
    action: 'Delete recipe',
    detail: (title) => `This will permanently remove "${title}" from your recipe book.`,
  },
  import: {
    title: 'Import recipe',
    shortTitle: 'Import',
    detail: 'Paste a recipe link. We will import recipe details so you can review and edit everything before saving.',
    urlLabel: 'Recipe URL',
    required: 'Required',
    description: 'Paste a recipe link. You can review and edit everything before saving.',
    placeholder: 'https://example.com/recipe',
    scanning: 'Importing recipe...',
    scanAction: 'Import recipe',
    validation: {
      required: 'Recipe URL is required.',
      invalidWebUrl: 'Enter a valid web URL.',
      invalidRecipeUrl: 'Enter a valid recipe URL.',
    },
  },
  form: {
    eyebrows: {
      importReview: 'Import review',
      editor: 'Recipe editor',
    },
    addTitle: 'Add recipe',
    editTitle: 'Edit recipe',
    defaultRecipeBook: 'My Cookbook',
    reviewTitle: 'Review imported recipe',
    favorite: 'Favorite recipe',
    favoriteAction: 'Mark as favorite',
    required: 'Required',
    optional: 'Optional',
    save: 'Save recipe',
    importedFrom: 'Imported from',
    validation: {
      required: (label) => `${label} is required.`,
      labels: {
        title: 'Recipe name',
        recipeBook: 'Recipe book',
        ingredient: 'At least one ingredient',
        instruction: 'At least one instruction',
      },
    },
    sections: {
      recipe: 'Recipe',
      ingredients: 'Items',
      instructions: 'Steps',
      organization: 'Organize',
      timing: 'Details',
      nutrition: 'Review',
    },
    fields: {
      recipeName: 'Recipe name',
      recipeBook: 'Recipe book',
      ingredients: 'Ingredients',
      instructions: 'Instructions',
      photos: 'Photos',
      description: 'Description',
      category: 'Category',
      mealTypes: 'Meal types',
      status: 'Status',
      sourceUrl: 'Source URL',
      cuisine: 'Cuisine',
      dietary: 'Dietary filters',
      tags: 'Tags',
      prep: 'Prep time',
      cook: 'Cook time',
      total: 'Total time',
      servings: 'Servings',
      difficulty: 'Difficulty',
      notes: 'Notes',
      calories: 'Calories',
      protein: 'Protein',
      rating: 'Rating',
    },
    placeholders: {
      recipeName: 'e.g. Weeknight lentil soup',
      description: 'A short summary, why you saved it, or when to cook it.',
      ingredient: 'Ingredient',
      quantity: 'Qty',
      unit: 'Unit',
      prepNote: 'Prep note',
      step: 'Describe this step',
      sourceUrl: 'https://example.com/recipe',
      cuisine: 'Italian, Mexican',
      dietary: 'Vegetarian, gluten-free',
      tags: 'Weeknight, Meal prep',
      prep: 'Minutes',
      cook: 'Minutes',
      total: 'Minutes',
      servings: '4',
      minutes: 'Minutes',
      notes: '',
      perServing: 'Per serving',
    },
    ingredientHelp: 'Add the ingredient name first. Quantity and unit are optional.',
    instructionHelp: 'Write one step at a time. You can add more as needed.',
    actions: {
      createNew: 'Create new',
      useExisting: 'Use existing',
      addIngredient: 'Add ingredient',
      addStep: 'Add step',
      remove: 'Remove',
      ingredientRow: (number) => `Ingredient ${number}`,
      stepRow: (number) => `Step ${number}`,
      removeIngredient: (number) => `Remove ingredient ${number}`,
      removeStep: (number) => `Remove step ${number}`,
    },
    errors: {
      recipeBookDuplicate: 'This recipe book already exists. Use existing or choose a different name.',
    },
  },
};

export const sharingStrings = {
  recipe: {
    eyebrow: 'Share recipe',
    helper: 'Share by email. If they have not joined Hobtalk yet, access will be ready after they sign in with that email.',
    action: 'Share recipe',
    sharing: 'Sharing...',
    peopleWithAccess: 'People with access',
    empty: 'No one else has access yet.',
    permissions: {
      view: 'Can view',
      edit: 'Can edit',
    },
    pending: 'Invite pending',
  },
  trip: {
    eyebrow: 'Share trip',
    helper: 'Share by email. If they have not joined Hobtalk yet, access will be ready after they sign in with that email.',
    action: 'Share trip',
    sharing: 'Sharing...',
    peopleWithAccess: 'People with access',
    empty: 'No one else has access yet.',
    permissions: {
      view: 'Can view',
      edit: 'Can edit',
      editTrip: 'Can edit trip',
      viewOnly: 'Can view only',
    },
    pending: 'Invite pending',
  },
  fields: {
    recipientEmail: 'Recipient email',
    permission: 'Permission',
    required: 'Required',
    emailPlaceholder: 'friend@example.com',
  },
  actions: {
    close: 'Close',
    cancel: 'Cancel',
    remove: 'Remove',
  },
  revoke: {
    title: 'Remove access?',
    detail: (name) => `This will remove access for ${name || 'this person'}.`,
    action: 'Remove access',
    removing: 'Removing...',
  },
  errors: {
    emailRequired: 'Email is required.',
    noProfile: 'We could not share with that email.',
    selfShare: 'You already own this trip.',
    forbidden: 'Only the owner can share this trip.',
    recipeNoProfile: 'We could not share with that email.',
    recipeSelfShare: 'You already own this recipe.',
    recipeForbidden: 'Only the owner can share this recipe.',
  },
};

export const checklistTemplateStrings = {
  picker: {
    eyebrow: 'Checklist',
    title: 'Use checklist',
    detail: '',
    loading: 'Loading checklists...',
    suggested: 'Suggested',
    saved: 'My checklists',
    allCategories: 'All',
    empty: 'No checklists available yet.',
    add: (name) => `Add ${name}`,
    delete: 'Delete checklist',
    deleteConfirm: (name) => `Delete "${name}"?`,
    deleting: 'Deleting...',
    shared: 'Shared',
    thisHobby: (hobbyName) => hobbyName || 'This hobby',
    checklistLabel: (label) => `${label} checklist`,
  },
  save: {
    eyebrow: 'Reusable checklist',
    title: 'Save checklist',
    name: 'Name',
    scope: 'Use in',
    thisHobby: (hobbyName) => hobbyName || 'This hobby',
    shared: 'Shared',
    placeholder: 'Weekend trip checklist',
    detail: (count) => `${count} ${count === 1 ? 'item' : 'items'} will be saved for future trips or hikes.`,
    saving: 'Saving...',
    action: 'Save checklist',
    duplicateName: 'This checklist already exists. Update the existing checklist or choose a different name.',
  },
  actions: {
    use: 'Use checklist',
    saveAsReusable: 'Save checklist',
  },
  counts: {
    items: (count) => `${count} ${count === 1 ? 'item' : 'items'}`,
  },
};

export const mediaStrings = {
  library: {
    watchlists: 'Watchlists',
    shelves: 'Shelves',
    status: 'Status',
    genre: 'Genre',
    format: 'Format',
    favorites: 'Favorites',
    anyStatus: 'Any status',
    anyGenre: 'Any genre',
    anyFormat: 'Any format',
    shown: (count) => `${count} shown`,
    favoritesCount: (count) => `${count} favorites`,
    watchlistsCount: (count) => `${count} watchlists`,
    activeCount: (count) => `${count} active`,
    notAdded: 'Not added',
    noReviewOrNotes: 'No review or notes yet.',
    libraryDetailsMissing: 'Library details not added yet',
    ratingLabel: (rating) => `Rated ${rating} out of 5`,
    watchedDate: (date) => `Watched ${date}`,
    spoilers: 'Spoilers',
    deleting: 'Deleting...',
    cancel: 'Cancel',
    detailsCannotUndo: 'This action cannot be undone.',
  },
  movies: {
    title: 'Movies',
    libraryEyebrow: 'Movie library',
    addAction: 'Add movie',
    searchPlaceholder: 'Search by title, director, cast, genre, or notes',
    addSearchPlaceholder: 'Search a movie by name',
    emptyTitle: 'Nothing saved yet',
    emptyFirst: 'Add your first movie when you are ready.',
    loading: 'Loading movies...',
    deleteTitle: 'Delete movie',
    deletePrompt: 'Delete movie?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your movie library.`,
    deleteAction: 'Delete movie',
    backToList: 'Back to movies',
    allItems: 'All movies',
    detailsTitle: 'Movie details',
    reviewTitle: 'Review',
    detailsMissing: 'Movie details not added yet',
    directedBy: (director) => `Directed by ${director}`,
    addSearch: {
      eyebrow: 'Add movie',
      title: 'Search a movie',
      label: 'Search a movie',
      placeholder: 'Search a movie by name',
      empty: 'No matches found. Add it manually and fill in the details yourself.',
      attribution: 'Movie metadata is provided by TMDB. This product uses the TMDB API but is not endorsed or certified by TMDB.',
      directedBy: (director) => `Directed by ${director}`,
    },
    import: {
      loading: {
        importing: (count) => `Finding details and saving ${count} selected ${count === 1 ? 'movie' : 'movies'}...`,
        previewing: 'Reading the Letterboxd export...',
      },
      title: 'Letterboxd import',
      exportTitle: 'Export and upload',
      exportDetail: 'Download your Letterboxd data export from letterboxd.com/settings/data, then choose the ZIP or an individual CSV file here.',
      getExportAction: 'Open Letterboxd data settings',
      reviewTitle: 'Review movies',
      filePicker: 'Choose Letterboxd CSV or ZIP',
      fileReadError: 'Could not read this file.',
      invalidFile: 'Choose a Letterboxd CSV or ZIP export.',
      previewError: 'Could not preview this Letterboxd export.',
      chooseDifferentFile: 'Choose a different file',
      rows: 'Rows',
      ready: 'Ready',
      duplicates: 'Duplicates',
      clearSelection: 'Clear selection',
      selectImportable: 'Select importable',
      selectedCount: (count) => `${count} selected`,
      untitledMovie: 'Untitled movie',
      importComplete: 'Import complete',
      result: ({ importedCount, skippedCount, failedCount }) => `Imported ${importedCount}. Skipped ${skippedCount}. Failed ${failedCount}.`,
      importAction: 'Import',
      importing: 'Importing...',
    },
    form: {
      eyebrow: 'Movie editor',
      addTitle: 'Add movie',
      editTitle: 'Edit movie',
      favorite: 'Favorite movie',
      favoriteAction: 'Mark as favorite',
      save: 'Save movie',
      ratingLabel: 'Movie rating',
      ratingValue: ratingStrings.value,
      notRated: 'Not rated',
      clearRating: 'Clear',
      starLabel: (value) => `${value} out of 5 stars`,
      sections: {
        movie: 'Movie',
        details: 'Details',
        library: 'Library',
        metadata: 'Discovery metadata',
        review: 'Review',
      },
      fields: {
        title: 'Title',
        collection: 'Watchlist',
        whereWatched: 'Where watched',
        watchOn: 'Watch on',
        watchedOn: 'Watched on',
        otherLocation: 'Other location / service',
        releaseYear: 'Release year',
        runtime: 'Runtime',
        director: 'Director',
        posterUrl: 'Poster image URL',
        status: 'Status',
        watchedDate: 'Watched date',
        genres: 'Genres',
        cast: 'Cast',
        rating: 'Rating',
        review: 'Review',
      },
      placeholders: {
        title: 'e.g. Arrival',
        collection: 'My Movies, Oscar watch, Weekend picks',
        releaseYear: '2016',
        runtime: '116 min',
        director: 'Denis Villeneuve',
        otherLocation: 'e.g. Alamo Drafthouse, Criterion Channel',
        posterUrl: 'https://...',
        genres: 'Sci-fi, Drama',
        cast: 'Amy Adams, Jeremy Renner',
        review: '',
      },
      options: {
        notSelected: 'Not selected',
        other: 'Other',
        spoiler: 'Review contains spoilers',
        statuses: ['Want to watch', 'Watching', 'Watched', 'Paused', 'Abandoned'],
        existing: 'Use existing',
        createNew: 'Create new',
        watchlistMode: 'Watchlist mode',
      },
      errors: {
        collectionDuplicate: 'This watchlist already exists. Use existing or choose a different name.',
      },
    },
  },
  tvshows: {
    title: 'TV Shows',
    libraryEyebrow: 'TV library',
    addAction: 'Add show',
    searchPlaceholder: 'Search by title, creator, cast, genre, or notes',
    addSearchPlaceholder: 'Search a show by name',
    emptyTitle: 'Nothing saved yet',
    emptyFirst: 'Add your first show when you are ready.',
    loading: 'Loading shows...',
    deleteTitle: 'Delete TV show',
    deletePrompt: 'Delete show?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your TV library.`,
    deleteAction: 'Delete show',
    backToList: 'Back to shows',
    allItems: 'All shows',
    detailsTitle: 'Show details',
    reviewTitle: 'Review',
    upNextTitle: 'Up next',
    seasonsTitle: 'Seasons and episodes',
    caughtUp: 'All available episodes watched',
    markWatched: 'Mark watched',
    markAllWatched: 'Mark all watched',
    clearSeason: 'Clear season',
    progressTitle: 'Progress',
    episodeDetailsUnavailable: 'Episode details are not available for this show yet.',
    upNextEpisode: (episode) => `Up next: ${episode}`,
    seasonTitle: (season) => `Season ${season}`,
    watched: 'Watched',
    unwatched: 'Unwatched',
    episodes: (watched, total) => `${watched}/${total} episodes`,
    percentWatched: (percent) => `${percent}% watched`,
    detailsMissing: 'Show details not added yet',
    createdBy: (creator) => `Created by ${creator}`,
    form: {
      eyebrow: 'TV show editor',
      addTitle: 'Add show',
      editTitle: 'Edit show',
      favorite: 'Favorite show',
      favoriteAction: 'Mark as favorite',
      save: 'Save show',
      sections: {
        show: 'Show',
        details: 'Details',
        library: 'Library',
        review: 'Review',
      },
      fields: {
        title: 'Title',
        collection: 'Watchlist',
        firstAirYear: 'First air year',
        network: 'Network',
        creator: 'Creator / showrunner',
        status: 'Show status',
        overview: 'Overview',
        posterUrl: 'Poster image URL',
        userStatus: 'Status',
        whereWatched: 'Where watched',
        watchOn: 'Watch on',
        watchedOn: 'Watched on',
        otherLocation: 'Other service / location',
        rating: 'Rating',
        genres: 'Genres',
        cast: 'Cast',
        review: 'Review',
      },
      placeholders: {
        network: 'Netflix, HBO, AMC',
        collection: 'My Shows, Weekend watch, Favorites',
        otherLocation: 'e.g. Living room, Hulu, flight',
        posterUrl: 'https://...',
        genres: 'Drama, Comedy',
        cast: 'Lead actors',
      },
      options: {
        productionStatuses: ['Returning', 'Ended', 'Canceled', 'In Production', 'Unknown'],
        userStatuses: ['Want to watch', 'Watching', 'Watched', 'Paused', 'Abandoned'],
        watchlistMode: 'Watchlist mode',
        spoiler: 'Review contains spoilers',
      },
      errors: {
        collectionDuplicate: 'This watchlist already exists. Use existing or choose a different name.',
      },
    },
  },
  reading: {
    title: 'Reading',
    libraryEyebrow: 'Reading library',
    addAction: 'Add book',
    searchPlaceholder: 'Search by title, author, genre, or review',
    addSearchPlaceholder: 'Search a book by name',
    emptyTitle: 'Nothing saved yet',
    emptyFirst: 'Add your first book when you are ready.',
    loading: 'Loading books...',
    deleteTitle: 'Delete book',
    deletePrompt: 'Delete book?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your library.`,
    deleteAction: 'Delete book',
    backToList: 'Back to books',
    importAction: 'Import from Goodreads',
    allItems: 'All books',
    progressTitle: 'Reading progress',
    detailsTitle: 'Book details',
    reviewTitle: 'Review',
    updateProgress: 'Update progress',
    markFinished: 'Mark finished',
    detailsMissing: 'Book details not added yet',
    progressField: {
      minutes: 'Minutes',
      percent: 'Percent',
      page: 'Page',
    },
    addSearch: {
      eyebrow: 'Add book',
      title: 'Search a book',
      label: 'Search a book',
      placeholder: 'Search a book by name',
      empty: 'No matches found. Add it manually and fill in the details yourself.',
      errors: {
        rateLimited: 'Book search is temporarily rate limited. Try again in a bit, or add the book manually.',
        unavailable: 'Book search is temporarily unavailable. Try again later, or add the book manually.',
      },
    },
    import: {
      loading: {
        importing: (count) => `Finding details and saving ${count} selected ${count === 1 ? 'book' : 'books'}...`,
        previewing: 'Reading the Goodreads export...',
      },
      title: 'Goodreads import',
      exportTitle: 'Export and upload',
      exportDetail: 'Download your Goodreads library CSV from goodreads.com/review/import, then choose that file here. If Goodreads opens home after sign-in, return here and open the export settings again.',
      getCsvAction: 'Open Goodreads export settings',
      reviewTitle: 'Review books',
      filePicker: 'Choose Goodreads CSV',
      invalidFile: 'Choose the Goodreads CSV export file.',
      fileReadError: 'Unable to read the selected file.',
      chooseDifferentFile: 'Choose a different file',
      rows: 'Rows',
      ready: 'Ready',
      duplicates: 'Duplicates',
      collection: 'Collection',
      collectionMode: 'Collection mode',
      existing: 'Existing',
      createNew: 'Create new',
      clearSelection: 'Clear selection',
      selectImportable: 'Select importable',
      selected: (count) => `${count} selected`,
      collectionPlaceholder: 'My Books, Book club, Favorites',
      untitledBook: 'Untitled book',
      bookFallback: 'Book',
      importComplete: 'Import complete',
      result: ({ importedCount, skippedCount, failedCount }) => `Imported ${importedCount}. Skipped ${skippedCount}. Failed ${failedCount}.`,
      importAction: 'Import',
      importing: 'Importing...',
    },
    form: {
      eyebrow: 'Book editor',
      addTitle: 'Add book',
      editTitle: 'Edit book',
      favorite: 'Favorite book',
      favoriteAction: 'Mark as favorite',
      save: 'Save book',
      sections: {
        book: 'Book',
        details: 'Details',
        library: 'Library',
        progress: 'Progress',
        review: 'Review',
      },
      fields: {
        title: 'Title',
        subtitle: 'Subtitle',
        authors: 'Authors',
        publishedYear: 'Published year',
        pageCount: 'Page count',
        format: 'Format',
        description: 'Description',
        coverUrl: 'Cover image URL',
        shelf: 'Status',
        collection: 'Collection',
        progressType: 'Progress type',
        currentPage: 'Current page',
        currentPercent: 'Current percent',
        currentMinutes: 'Minutes listened',
        started: 'Started',
        finished: 'Finished',
        rating: 'Rating',
        genres: 'Genres / tags',
        favorite: 'Favorite',
        review: 'Review',
      },
      placeholders: {
        authors: 'Author names, comma separated',
        coverUrl: 'https://...',
        collection: 'My Books, Favorites, Book club',
        genres: 'Fantasy, nonfiction, cozy',
        review: 'What did you think?',
      },
      options: {
        formats: ['Physical', 'Ebook', 'Audiobook', 'Mixed'],
        shelves: ['Want to read', 'Reading', 'Read', 'Paused', 'Abandoned'],
        progressTypes: ['Pages', 'Percent', 'Minutes'],
        collectionMode: 'Collection mode',
        spoiler: 'Review contains spoilers',
      },
      errors: {
        collectionDuplicate: 'This reading list already exists. Use existing or choose a different name.',
      },
    },
  },
};

export const tvShowSearchStrings = {
  eyebrow: 'Add TV show',
  title: 'Search a show',
  label: 'Search a show',
  placeholder: 'Search a show by name',
  empty: 'No matches found. Add it manually and fill in the details yourself.',
  attribution: 'TV metadata is provided by TMDB. This product uses the TMDB API but is not endorsed or certified by TMDB.',
};

export const fitnessStrings = {
  module: {
    title: 'Fitness',
    activityEyebrow: 'Fitness',
    addAction: 'Add session',
    backToList: 'Back to fitness',
    deleteTitle: 'Delete session',
    deletePrompt: 'Delete session?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your fitness log.`,
    deleteAction: 'Delete session',
    editAction: 'Edit session',
    searchLabel: 'Search activities',
    searchPlaceholder: 'Search by title, activity, notes, or tags',
    tagLabel: 'Tag',
    anyTag: 'Any tag',
    loading: 'Loading your fitness sessions...',
    empty: 'No sessions match those filters. Try clearing search or adding your next workout, walk, or routine.',
    emptyFirst: 'Add your first activity when you are ready.',
    allActivities: 'All sessions',
    structured: 'Workouts',
    sessions: 'Activities',
    shown: (count) => `${count} shown`,
    minutes: (count) => `${count} minutes`,
    completed: (count) => `${count} completed`,
    noDate: 'No date',
    noDuration: 'No duration',
    noNotes: 'No details yet.',
    favorite: 'Favorite',
    calories: (count) => `${count} cal`,
    ratingLabel: (rating) => `Rated ${rating} out of 5`,
    discardConfirm: 'Discard your unsaved fitness changes?',
    untitled: 'Untitled session',
  },
  form: {
    eyebrow: 'Fitness editor',
    addTitle: 'Add session',
    editTitle: 'Edit session',
    favorite: 'Favorite session',
    favoriteAction: 'Mark as favorite',
    sectionTitle: 'Fitness',
    save: 'Save session',
    ratingAriaLabel: 'Fitness rating',
    sections: {
      basics: 'Start',
      details: 'Details',
      timeAndEffort: 'Time and effort',
      activityMetrics: 'Activity metrics',
      workoutFocus: 'Workout focus',
      exercises: 'Exercises',
      log: 'Log',
      review: 'Review',
      sessionLog: 'Activity log',
      tags: 'Tags',
      activityTiming: 'Activity timing',
      recovery: 'Recovery',
    },
    modes: {
      structured: 'Workout',
      session: 'Activity',
    },
    actions: {
      addExercise: 'Add exercise',
      addSet: 'Add set',
      removeExercise: 'Remove exercise',
      removeSet: 'Remove set',
      clearRating: 'Clear',
    },
  },
  fields: {
    title: 'Title',
    mode: 'Mode',
    type: 'Type',
    date: 'Date',
    activityType: 'Activity type',
    duration: 'Duration',
    intensity: 'Effort',
    calories: 'Calories',
    distance: 'Distance',
    unit: 'Unit',
    pace: 'Pace or speed',
    averageHeartRate: 'Average HR',
    maxHeartRate: 'Max HR',
    elevationGain: 'Elevation gain',
    focusAreas: 'Focus areas',
    classOrSport: 'Class or sport',
    location: 'Location',
    coach: 'Coach',
    muscleGroups: 'Muscle groups',
    equipment: 'Equipment',
    exercise: 'Exercise',
    muscleGroup: 'Muscle group',
    reps: 'Reps',
    weight: 'Weight',
    restSeconds: 'Rest sec',
    routeOrLocation: 'Route or location',
    sessionNotes: 'Session notes',
    rating: 'Rating',
    review: 'Review',
    privateNotes: 'Notes',
    status: 'Status',
    tags: 'Tags',
    notes: 'Notes',
  },
  placeholders: {
    title: 'Upper body strength',
    sessionTitle: 'Evening walk',
    structuredTitle: 'Legs',
    duration: '45',
    calories: 'Optional',
    distance: '3.1',
    pace: '9:20 / mi',
    averageHeartRate: '145',
    maxHeartRate: '172',
    elevationGain: '300',
    focusAreas: 'Hips, shoulders',
    classOrSport: 'Basketball',
    location: 'Gym, park, studio',
    coach: 'Optional',
    muscleGroups: 'Chest, back, legs',
    equipment: 'Dumbbells, mat',
    exercise: 'Bench press',
    muscleGroup: 'Chest',
    exerciseEquipment: 'Barbell',
    routeOrLocation: 'Neighborhood loop',
    tags: 'Leg day, outdoors, class',
    notes: 'How it felt, what worked, or anything to remember.',
  },
  activityTypes: ['Strength', 'Cardio', 'Mobility', 'Walk', 'Run', 'Cycling', 'Yoga', 'Sport', 'Other'],
  intensityOptions: ['Easy', 'Moderate', 'Hard', 'Max effort'],
};

export const hikingStrings = {
  module: {
    title: 'Hiking',
    eyebrow: 'Trail log',
    statusEyebrow: 'Status',
    addAction: 'Add hike',
    backToList: 'Back to hikes',
    deleteTitle: 'Delete hike',
    deletePrompt: 'Delete hike?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your hiking log.`,
    deleteAction: 'Delete hike',
    editAction: 'Edit hike',
    searchLabel: 'Search hikes',
    searchPlaceholder: 'Search by trail, location, trip, notes, or tags',
    difficultyLabel: 'Difficulty',
    anyDifficulty: 'Any difficulty',
    tagLabel: 'Tag',
    anyTag: 'Any tag',
    loading: 'Loading your hikes...',
    loadingMore: 'Loading hikes...',
    loadMore: 'Load more hikes',
    emptyTitle: 'No matches found',
    empty: 'Try a different search or clear your filters.',
    emptyFirst: 'Add your first hike when you are ready.',
    allHikes: 'All hikes',
    shown: (count) => `${count} shown`,
    minutes: (count) => `${count} minutes`,
    completed: (count) => `${count} completed`,
    noDate: 'No date',
    noDuration: 'No duration',
    noDistance: 'Distance not set',
    noNotes: 'No notes yet.',
    favorite: 'Favorite',
    openMap: 'Open map',
    openRoute: 'Open route',
    linkedTrip: (title) => `Linked to ${title}`,
    discardConfirm: 'Discard your unsaved hike changes?',
    untitled: 'Untitled hike',
  },
  form: {
    eyebrow: 'Hike editor',
    addTitle: 'Add hike',
    editTitle: 'Edit hike',
    favorite: 'Favorite hike',
    favoriteAction: 'Mark as favorite',
    save: 'Save hike',
    sections: {
      search: 'Search',
      trail: 'Trail',
      hike: 'Hike',
      map: 'Map',
      planning: 'Planning',
      details: 'Details',
      trailProfile: 'Trail profile',
      distanceTime: 'Distance and time',
      conditions: 'Conditions',
      checklist: 'Checklist',
      packing: 'Checklist',
      notes: 'Notes',
      review: 'Review',
    },
    addChecklistItem: 'Add checklist item',
    enriching: 'Updating trail details...',
  },
  addSearch: {
    eyebrow: 'Trail discovery',
    title: 'Search a trail',
    label: 'Search a trail',
    placeholder: 'Search by trail name or location',
    hint: 'Type at least 2 characters to search trail ideas.',
    empty: 'No matching trails found. Add the hike manually instead.',
    attribution: 'Trail search uses OpenStreetMap data.',
  },
  fields: {
    title: 'Trail name',
    location: 'Location',
    date: 'Date',
    status: 'Status',
    distance: 'Distance',
    distanceUnit: 'Distance unit',
    elevationGain: 'Elevation gain',
    elevationUnit: 'Elevation unit',
    duration: 'Duration',
    difficulty: 'Difficulty',
    trailType: 'Trail type',
    terrain: 'Terrain',
    routeUrl: 'Route URL',
    mapUrl: 'Map URL',
    permitRequired: 'Permit required',
    dogFriendly: 'Dog friendly',
    kidFriendly: 'Kid friendly',
    startTime: 'Start time',
    parkingNotes: 'Parking notes',
    companions: 'Companions',
    weather: 'Weather',
    trailCondition: 'Trail condition',
    crowdLevel: 'Crowd level',
    actualDistance: 'Actual distance',
    actualDuration: 'Actual duration',
    rating: 'Rating',
    review: 'Review',
    tripTitle: 'Linked trip',
    tripDayTitle: 'Trip day',
    tags: 'Tags',
    notes: 'Notes',
    checklistItem: 'Packing item',
    packed: 'Packed',
  },
  placeholders: {
    title: 'Dipsea Trail',
    location: 'Mill Valley, CA',
    distance: '7.4',
    elevationGain: '2200',
    duration: '180',
    actualDistance: '7.6',
    actualDuration: '195',
    rating: '4.5',
    parkingNotes: 'Trailhead parking, fees, shuttle notes',
    companions: 'Alex, Priya',
    weather: 'Cool, cloudy, light wind',
    trailCondition: 'Muddy near the creek',
    checklistItem: 'Packing item',
    tripTitle: 'Optional trip name',
    tripDayTitle: 'Optional itinerary day',
    tags: 'Coastal, views, weekend',
    notes: '',
  },
  statusOptions: ['Want to hike', 'Planned', 'Completed', 'Skipped'],
  difficultyOptions: ['Easy', 'Moderate', 'Hard', 'Strenuous'],
  trailTypeOptions: ['Loop', 'Out and back', 'Point to point'],
  terrainOptions: ['Paved', 'Dirt', 'Rocky', 'Snow', 'Mixed'],
  crowdOptions: ['Quiet', 'Moderate', 'Busy'],
  distanceUnits: ['mi', 'km'],
  elevationUnits: ['ft', 'm'],
};

export const travelingStrings = {
  module: {
    backToList: 'Back to trips',
    shareAction: 'Share trip',
    deleteAction: 'Delete trip',
    editAction: 'Edit trip',
    deleteTitle: 'Delete trip',
    deletePrompt: 'Delete trip?',
    deleteDetail: (title) => `This will permanently remove "${title}" from your trips.`,
    sharedEdit: 'Shared with edit access',
    sharedViewOnly: 'Shared view-only',
    sharedBy: (name) => `Shared by ${name || 'another Hobtalk user'}`,
    days: (count) => `${count} ${count === 1 ? 'day' : 'days'}`,
    hikes: (count) => `${count} ${count === 1 ? 'hike' : 'hikes'}`,
    packedCount: (packed, total) => `${packed}/${total} packed`,
    detailsTitle: 'Details',
    dates: 'Dates',
    itineraryTitle: 'Itinerary',
    linkedHikesTitle: 'Linked hikes',
    packingTitle: 'Checklist',
    noItinerary: 'No itinerary yet.',
    noLinkedHikes: 'No hikes linked to this trip yet.',
    noPackingItems: 'No packing items yet.',
    notesTitle: 'Notes',
    noNotes: 'No notes yet.',
    hikePrefix: 'Hike',
    planning: 'Planning...',
    planHike: 'Plan hike',
    tripStatus: 'Trip status',
    allTrips: 'All trips',
    searchLabel: 'Search trips',
    searchPlaceholder: 'Search by destination, tag, or notes',
    tagLabel: 'Tag',
    anyTag: 'Any tag',
    accessFilterLabel: 'Trip access filter',
    accessAll: 'All',
    accessOwned: 'Owned',
    accessShared: 'Shared with me',
    loading: 'Packing your bags...',
    loadingMore: 'Loading trips...',
    loadMore: 'Load more trips',
    emptyFirst: 'Add your first trip when you are ready.',
    emptyFiltered: 'No trips match those filters. Try clearing search or adding a trip.',
    shown: (count) => `${count} shown`,
    favorites: (count) => `${count} favorites`,
    active: (count) => `${count} active`,
    shared: (count) => `${count} shared`,
    favorite: 'Favorite',
    sharedChip: 'Shared',
    detailsMissing: 'Trip details not added yet',
    datesNotSet: 'Dates not set',
    untitled: 'Untitled trip',
    packed: 'Packed',
    notPacked: 'Not packed',
  },
  form: {
    eyebrow: 'Trip editor',
    addTitle: 'Add trip',
    editTitle: 'Edit trip',
    favorite: 'Favorite trip',
    favoriteAction: 'Mark as favorite',
    save: 'Save trip',
    poweredByGoogle: 'Powered by Google',
    searchingDestinations: 'Searching destinations...',
    dayTitle: (index) => `Day ${index + 1}`,
    selectedTripTitle: (destination) => `${destination} trip`,
    sections: {
      destination: 'Destination',
      trip: 'Trip',
      dailyPlan: 'Daily plan',
      itinerary: 'Itinerary',
      mapPlanning: 'Map planning',
      savedPlaces: 'Saved places',
      budget: 'Budget',
      expenses: 'Expenses',
      checklist: 'Checklist',
      packing: 'Checklist',
      notes: 'Notes',
      checklistItem: 'Checklist item',
    },
    fields: {
      title: 'Trip title',
      destination: 'Destination',
      status: 'Status',
      startDate: 'Start date',
      endDate: 'End date',
      tripType: 'Trip type',
      tags: 'Tags',
      coverUrl: 'Cover image URL',
      notes: 'Notes',
    },
    placeholders: {
      title: 'Seaside trip',
      destination: 'Search Destination',
      tags: 'Food, Museum, Beach',
      coverUrl: 'https://...',
      dayTitle: 'Title',
      dayNotes: '',
      itemTitle: 'Item title',
      location: 'Location',
      startTime: 'Start time',
      endTime: 'End time',
      cost: 'Cost',
      placeName: 'Place name',
      address: 'Address',
      expenseTitle: 'Expense title',
      amount: 'Amount',
      currency: 'USD',
      packingItem: 'Packing item',
      category: 'Category',
    },
    empty: {
      itineraryNeedsDates: 'Choose a start and end date to create itinerary days.',
    },
    statuses: ['Idea', 'Planning', 'Booked', 'In progress', 'Completed', 'Canceled'],
    tripTypes: ['Solo', 'Couple', 'Family', 'Friends', 'Business'],
    labels: {
      packed: 'Packed',
    },
    actions: {
      addDay: 'Add day',
      removeDay: 'Remove day',
      addItem: 'Add item',
      addChecklistItem: 'Add checklist item',
      removeItem: 'Remove item',
      addPlace: 'Add place',
      addExpense: 'Add expense',
    },
    errors: {
      notConfigured: 'Destination search needs a Google Places API key before it can be used.',
      rateLimited: 'Destination search is temporarily rate limited. Try again in a bit.',
      forbidden: 'Destination search is blocked by the Google API key settings.',
      unavailable: 'Destination search is temporarily unavailable.',
    },
  },
};
